import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import {
  AlertTriangle,
  ChevronRight,
  Download,
  Plus,
  Search,
  Sparkles,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CitationChip } from '@/components/ui/citation-chip';
import { ErrorMessage } from '@/components/ui/error-message';
import { KpiSourcePill } from '@/components/ui/kpi-source-pill';
import { PostureChip } from '@/components/ui/posture-chip';
import { Spinner } from '@/components/ui/spinner';
import { VerbChip } from '@/components/ui/verb-chip';
import { ContractHeader } from '@/components/contract/contract-header';
import { formatDay } from '@/components/contract/checkpoint-status';
import {
  DeliverableDrawer,
  deliverableCitationChip,
  type DeliverableDrawerMode,
} from '@/components/contract/deliverable-drawer';
import {
  NEEDS_ACTION,
  compareDeliverables,
  deliverableState,
  isMissingInfo,
  sourceWord,
  statusWord,
  verificationLabel,
} from '@/components/contract/deliverable-status';
import { ReevalTrayModal } from '@/components/modals/reeval-tray-modal';
import { VerifyStateModal } from '@/components/modals/verify-state-modal';
import { useEdlListQuery, type EdlEntry } from '@/lib/api/deliverables';
import { useProposalQueueQuery } from '@/lib/api/review-proposals';
import { downloadCsv } from '@/lib/utils/csv';
import { cn } from '@/lib/utils/cn';

/**
 * /contract/deliverables — the contracted work products (E06-F03).
 *
 * Deliverables come from the SOW: base formation extracts them, the
 * consultant approves them in the AI review queue, and they land here with
 * their SOW reference, due date, acceptance criteria and citation.
 * Milestones share the list behind an item-type switch (T-0229 / C2-18)
 * and the same submittal lifecycle: the vendor reports one reached, IV&V
 * accepts or rejects it. The other EDL buckets (payment terms,
 * KPIs, SLAs, integrations) live on the Baseline tab. Clicking a row opens
 * the deliverable drawer: details, the submittal next steps, verification
 * and history. `?open=<register_entry_id>` opens that row's drawer on
 * arrival (the deviation card links here, C2-31).
 */
export const Route = createFileRoute('/engagements/$engagementId/contract/deliverables')({
  component: ContractDeliverablesTab,
  validateSearch: (search: Record<string, unknown>): { open?: string } =>
    typeof search.open === 'string' && search.open.length > 0 ? { open: search.open } : {},
});

type Filter = 'all' | 'action' | 'accepted' | 'missing';
/** Which EDL bucket the list shows: contracted work products, or dated milestones. */
type ItemView = 'deliverable' | 'milestone';

const OTHER_BUCKETS: ReadonlyArray<[string, string]> = [
  ['payment_term', 'payment terms'],
  ['kpi', 'KPIs'],
  ['sla', 'SLAs'],
  ['integration', 'integrations'],
];

function ContractDeliverablesTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/contract/deliverables' });
  const { open } = Route.useSearch();
  const [view, setView] = useState<ItemView>('deliverable');
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [drawer, setDrawer] = useState<DeliverableDrawerMode | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<EdlEntry | null>(null);
  const [tray, setTray] = useState<{ overrideId: string; reasonTag: string } | null>(null);

  const edl = useEdlListQuery(engagementId);
  const pendingProposals = useProposalQueueQuery(engagementId, {
    decision: 'pending',
    proposal_type: 'edl_item',
    limit: 200,
  });
  const pendingCount = pendingProposals.data?.items.length ?? 0;

  const all = useMemo(() => edl.data?.entries ?? [], [edl.data]);
  const deliverables = useMemo(
    () => all.filter((e) => e.item_type === 'deliverable').sort(compareDeliverables),
    [all],
  );
  const milestones = useMemo(
    () => all.filter((e) => e.item_type === 'milestone').sort(compareDeliverables),
    [all],
  );
  // A link from another screen opens one row's drawer, switching to the
  // milestone list when that is where the row lives. Once per link: a
  // refetch after a transition must not reopen a drawer the user closed.
  const openedFromLink = useRef<string | null>(null);
  useEffect(() => {
    if (!open || openedFromLink.current === open) return;
    const target = all.find((e) => e.register_entry_id === open);
    if (!target) return;
    openedFromLink.current = open;
    if (target.item_type === 'milestone') setView('milestone');
    setDrawer({ mode: 'view', entryId: open });
  }, [open, all]);
  const otherCounts = OTHER_BUCKETS.map(
    ([type, label]) => [all.filter((e) => e.item_type === type).length, label] as const,
  ).filter(([n]) => n > 0);

  const states = useMemo(
    () =>
      new Map(
        [...deliverables, ...milestones].map((e) => [e.register_entry_id, deliverableState(e)]),
      ),
    [deliverables, milestones],
  );
  const keyOf = (e: EdlEntry) => states.get(e.register_entry_id)!.key;
  const count = (k: string) => deliverables.filter((e) => keyOf(e) === k).length;
  const accepted = deliverables.filter((e) => keyOf(e) === 'accepted');
  const missing = deliverables.filter(isMissingInfo);
  const noDate = deliverables.filter((e) => !e.expected_delivery_date && !e.due_text).length;
  const noCriteria = deliverables.filter((e) => e.acceptance_criteria.length === 0).length;
  const nextDue =
    deliverables
      .filter(
        (e) =>
          e.expected_delivery_date &&
          ['outstanding', 'due-soon', 'revise'].includes(keyOf(e)) &&
          e.expected_delivery_date >= new Date().toISOString().slice(0, 10),
      )
      .sort((a, b) => a.expected_delivery_date!.localeCompare(b.expected_delivery_date!))[0] ??
    null;

  // The tiles and the header describe deliverables; the filter chips work on
  // whichever list the switch shows.
  const viewItems = view === 'milestone' ? milestones : deliverables;
  const filtered = (f: Filter): EdlEntry[] =>
    f === 'action'
      ? viewItems.filter((e) => NEEDS_ACTION.has(keyOf(e)))
      : f === 'accepted'
        ? viewItems.filter((e) => keyOf(e) === 'accepted')
        : f === 'missing'
          ? viewItems.filter(isMissingInfo)
          : viewItems;
  const q = search.trim().toLowerCase();
  const listed = filtered(filter);
  const rows = q
    ? listed.filter((e) =>
        [e.title, e.sow_ref ?? '', e.category ?? '', e.description ?? '']
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
    : listed;
  const openRow = (e: EdlEntry) => setDrawer({ mode: 'view', entryId: e.register_entry_id });

  const meta =
    deliverables.length === 0
      ? 'No deliverables yet'
      : [
          `${deliverables.length} ${deliverables.length === 1 ? 'deliverable' : 'deliverables'}`,
          `${count('awaiting-review')} awaiting review`,
          `${count('overdue')} overdue`,
          `${accepted.length} accepted`,
        ].join(' · ');

  return (
    <div className="space-y-5">
      <ContractHeader
        engagementId={engagementId}
        active="deliverables"
        meta={<span>{meta}</span>}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={rows.length === 0}
              onClick={() =>
                downloadCsv(
                  'deliverables.csv',
                  [
                    'sow_ref',
                    'title',
                    'status',
                    'due',
                    'payment_tied',
                    'acceptance_criteria',
                    'verification',
                  ],
                  rows.map((r) => [
                    r.sow_ref ?? '',
                    r.title,
                    statusWord(r.status),
                    r.expected_delivery_date ?? r.due_text ?? '',
                    r.is_payment_tied === null ? '' : r.is_payment_tied ? 'yes' : 'no',
                    r.acceptance_criteria.join(' | '),
                    verificationLabel(r).label,
                  ]),
                )
              }
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Export
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
              onClick={() => setDrawer({ mode: 'create' })}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add deliverable
            </Button>
          </>
        }
      />

      {edl.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading deliverables…
        </div>
      ) : edl.error ? (
        <ErrorMessage error={edl.error} />
      ) : deliverables.length === 0 && milestones.length === 0 ? (
        <NoDeliverables
          engagementId={engagementId}
          pendingCount={pendingCount}
          onAdd={() => setDrawer({ mode: 'create' })}
        />
      ) : (
        <>
          {pendingCount > 0 ? (
            <div className="flex items-center justify-between gap-4 rounded-card border border-ai-surface-border bg-ai-surface px-4 py-3 text-body-sm text-ai-ink">
              <span className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                {pendingCount} more extracted {pendingCount === 1 ? 'item is' : 'items are'} waiting
                in AI review. Deliverables appear here once approved.
              </span>
              <Button asChild variant="outline" size="sm">
                <Link to="/engagements/$engagementId/proposals" params={{ engagementId }}>
                  Review
                </Link>
              </Button>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[1.4fr_1fr_1fr_1fr]">
            <NextDueTile entry={nextDue} onOpen={openRow} />
            <CountTile
              label="Awaiting your review"
              value={count('awaiting-review')}
              tone={count('awaiting-review') > 0 ? 'at-risk' : 'neutral'}
              source="Submitted or under review"
            />
            <CountTile
              label="With vendor to revise"
              value={count('revise')}
              tone="neutral"
              source="Revise & resubmit"
            />
            <CountTile
              label="Overdue"
              value={count('overdue')}
              tone={count('overdue') > 0 ? 'critical' : 'neutral'}
              source="Past due, not accepted"
            />
          </div>

          {missing.length > 0 ? (
            <div className="flex items-center justify-between gap-4 rounded-card border border-ink-100 border-l-4 border-l-posture-at-risk bg-paper-0 px-4 py-3 text-body-sm text-ink-700">
              <span className="flex items-center gap-2">
                <AlertTriangle
                  className="h-4 w-4 shrink-0 text-posture-at-risk"
                  aria-hidden="true"
                />
                <span>
                  {noDate > 0 ? (
                    <>
                      <strong className="font-semibold text-ink-900">{noDate}</strong> without a due
                      date.{' '}
                    </>
                  ) : null}
                  {noCriteria > 0 ? (
                    <>
                      <strong className="font-semibold text-ink-900">{noCriteria}</strong> without
                      acceptance criteria.{' '}
                    </>
                  ) : null}
                  Without them nothing can be held overdue or objectively accepted.
                </span>
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setView('deliverable');
                  setFilter('missing');
                }}
              >
                Show them
              </Button>
            </div>
          ) : null}

          <Card>
            <CardContent className="p-0">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-100 px-5 py-3">
                <div>
                  <h2 className="text-h3 font-semibold text-ink-900">
                    {view === 'milestone' ? 'Milestones' : 'Deliverables'}
                  </h2>
                  <p className="text-meta text-ink-500">
                    {view === 'milestone'
                      ? 'In SOW order. The vendor reports a milestone reached; click one to accept it, send it back or see its history.'
                      : 'In SOW order. Click a deliverable for its criteria, source, next steps and history.'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {milestones.length > 0 ? (
                    <ItemViewSwitch
                      value={view}
                      onChange={setView}
                      counts={{ deliverable: deliverables.length, milestone: milestones.length }}
                    />
                  ) : null}
                  <label className="relative">
                    <Search
                      className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-300"
                      aria-hidden="true"
                    />
                    <input
                      type="search"
                      aria-label="Search deliverables"
                      value={search}
                      onChange={(ev) => setSearch(ev.target.value)}
                      placeholder="Search"
                      className="h-[26px] w-40 rounded-full border border-ink-100 bg-paper-50 pl-7 pr-3 text-meta text-ink-900 placeholder:text-ink-300 focus:border-ink-300 focus:outline-none"
                    />
                  </label>
                  <FilterChips
                    value={filter}
                    onChange={setFilter}
                    counts={{
                      all: viewItems.length,
                      action: filtered('action').length,
                      accepted: filtered('accepted').length,
                      missing: filtered('missing').length,
                    }}
                  />
                </div>
              </div>
              {rows.length === 0 ? (
                <p className="px-5 py-6 text-body-sm text-ink-500">
                  {q ? `Nothing matches “${search.trim()}”.` : 'Nothing in this view.'}
                </p>
              ) : (
                <DeliverableTable rows={rows} onOpen={openRow} />
              )}
            </CardContent>
          </Card>

          {otherCounts.length > 0 ? (
            <p className="text-meta text-ink-500">
              The SOW’s {otherCounts.map(([n, label]) => `${n} ${label}`).join(', ')} are on the{' '}
              <Link
                to="/engagements/$engagementId/contract/baseline"
                params={{ engagementId }}
                className="font-medium text-ink-700 underline underline-offset-2"
              >
                Baseline
              </Link>{' '}
              tab.
            </p>
          ) : null}
        </>
      )}

      <DeliverableDrawer
        engagementId={engagementId}
        entries={[...deliverables, ...milestones]}
        state={drawer}
        onStateChange={setDrawer}
        onVerify={(e) => setVerifyTarget(e)}
        onOverrideCaptured={(overrideId, reasonTag) => setTray({ overrideId, reasonTag })}
      />
      {verifyTarget ? (
        <VerifyStateModal
          engagementId={engagementId}
          registerType="deliverable"
          entryId={verifyTarget.register_entry_id}
          subjectName={verifyTarget.title}
          subjectKind="Deliverable"
          open
          onOpenChange={(o) => !o && setVerifyTarget(null)}
        />
      ) : null}
      {tray ? (
        <ReevalTrayModal
          engagementId={engagementId}
          triggerOverrideId={tray.overrideId}
          reasonTag={tray.reasonTag}
          open
          onOpenChange={(o) => !o && setTray(null)}
        />
      ) : null}
    </div>
  );
}

// ===========================================================================
// Table
// ===========================================================================

function DeliverableTable({
  rows,
  onOpen,
}: {
  rows: ReadonlyArray<EdlEntry>;
  onOpen: (e: EdlEntry) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-body-sm">
        <thead>
          <tr className="border-b border-ink-100 bg-paper-50 text-left">
            <Th className="w-[88px]">Ref</Th>
            <Th>Item</Th>
            <Th className="w-[150px]">Due</Th>
            <Th className="w-[190px]">Status</Th>
            <Th className="w-[130px]">Verification</Th>
            <Th className="w-[190px]">Source</Th>
            <Th className="w-[36px]">
              <span className="sr-only">Open</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <DeliverableRow key={e.register_entry_id} entry={e} onOpen={onOpen} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeliverableRow({ entry: e, onOpen }: { entry: EdlEntry; onOpen: (e: EdlEntry) => void }) {
  const isMilestone = e.item_type === 'milestone';
  const state = deliverableState(e);
  const verification = verificationLabel(e);
  const cite = deliverableCitationChip(e);
  return (
    <tr
      tabIndex={0}
      aria-label={`Open ${e.sow_ref ?? ''} ${e.title}`}
      onClick={() => onOpen(e)}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          onOpen(e);
        }
      }}
      className="cursor-pointer border-b border-ink-100 last:border-b-0 hover:bg-paper-50 focus:bg-paper-50 focus:outline-none"
    >
      <Td className="whitespace-nowrap font-mono text-mono-sm text-ink-500">{e.sow_ref ?? '—'}</Td>
      <Td>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-ink-900">{e.title}</span>
          {e.is_payment_tied ? (
            <span className="inline-flex h-[18px] items-center rounded-full bg-accent-amber-100 px-2 text-[11px] font-medium text-accent-amber-700">
              Payment
            </span>
          ) : null}
        </div>
        {e.description ? (
          <div className="max-w-[520px] truncate text-meta text-ink-500">{e.description}</div>
        ) : !isMilestone && e.acceptance_criteria.length === 0 ? (
          <div className="text-meta text-posture-at-risk">No acceptance criteria</div>
        ) : null}
      </Td>
      <Td className="whitespace-nowrap">
        {e.expected_delivery_date ? (
          <div className="font-mono text-mono-sm text-ink-900">
            {formatDay(e.expected_delivery_date)}
          </div>
        ) : (
          <div className="max-w-[140px] truncate text-meta text-ink-500">
            {e.due_text ? `SOW: ${e.due_text}` : 'No date'}
          </div>
        )}
      </Td>
      <Td className="whitespace-nowrap">
        <VerbChip verb={state.verb} />
        {state.detail ? <div className="mt-1 text-meta text-ink-500">{state.detail}</div> : null}
      </Td>
      <Td className="whitespace-nowrap">
        <PostureChip status={verification.posture} dot>
          {verification.label}
        </PostureChip>
      </Td>
      <Td>
        {e.source_change_order ? (
          // C2-22: an item a change order added names that change order.
          <span className="block text-meta text-ink-700">{sourceWord(e)}</span>
        ) : null}
        {cite ? (
          // No handler: the click bubbles to the row, and the drawer opens the passage.
          <CitationChip cite={cite} />
        ) : e.source_change_order ? null : (
          <span className="text-meta text-ink-500">{sourceWord(e)}</span>
        )}
      </Td>
      <Td>
        <ChevronRight className="h-4 w-4 text-ink-300" aria-hidden="true" />
      </Td>
    </tr>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-3 align-middle', className)}>{children}</td>;
}

// ===========================================================================
// Tiles, filters, empty state
// ===========================================================================

function NextDueTile({ entry, onOpen }: { entry: EdlEntry | null; onOpen: (e: EdlEntry) => void }) {
  return (
    <Card className="border-l-[3px] border-l-accent-amber-600">
      <CardContent className="flex h-full items-center gap-4 p-4">
        {entry ? (
          <>
            {entry.sow_ref ? (
              <div className="font-mono text-h2 font-semibold text-ink-900">{entry.sow_ref}</div>
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="text-meta uppercase tracking-wide text-ink-500">Next due</div>
              <div className="truncate font-semibold text-ink-900">{entry.title}</div>
              <div className="text-meta text-ink-500">
                {formatDay(entry.expected_delivery_date!)} · {deliverableState(entry).detail}
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => onOpen(entry)}>
              Open
            </Button>
          </>
        ) : (
          <div>
            <div className="text-meta uppercase tracking-wide text-ink-500">Next due</div>
            <div className="mt-1 text-body-sm text-ink-500">
              Nothing dated is coming up. Deliverables need a due date to show here.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CountTile({
  label,
  value,
  tone,
  source,
}: {
  label: string;
  value: number;
  tone: 'neutral' | 'at-risk' | 'critical';
  source: string;
}) {
  const valueClass =
    tone === 'critical'
      ? 'text-posture-critical'
      : tone === 'at-risk'
        ? 'text-posture-at-risk'
        : 'text-ink-900';
  return (
    <Card>
      <CardContent className="space-y-1.5 p-4">
        <div className="text-meta uppercase tracking-wide text-ink-500">{label}</div>
        <div className={cn('text-h2 font-semibold tabular-nums', valueClass)}>{value}</div>
        <KpiSourcePill source={source} />
      </CardContent>
    </Card>
  );
}

/** Deliverables or milestones: one list, one switch (C2-18). */
function ItemViewSwitch({
  value,
  onChange,
  counts,
}: {
  value: ItemView;
  onChange: (v: ItemView) => void;
  counts: Record<ItemView, number>;
}) {
  const options: Array<[ItemView, string]> = [
    ['deliverable', 'Deliverables'],
    ['milestone', 'Milestones'],
  ];
  return (
    <div role="tablist" aria-label="Item type" className="flex gap-2">
      {options.map(([key, text]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={value === key}
          onClick={() => onChange(key)}
          className={cn(
            'inline-flex h-[26px] items-center gap-1.5 rounded-full border px-3 text-meta font-medium',
            value === key
              ? 'border-accent-amber-600 bg-accent-amber-100 text-accent-amber-700'
              : 'border-ink-100 bg-paper-100 text-ink-700 hover:border-ink-200',
          )}
        >
          {text}
          <span className="font-mono">{counts[key]}</span>
        </button>
      ))}
    </div>
  );
}

function FilterChips({
  value,
  onChange,
  counts,
}: {
  value: Filter;
  onChange: (f: Filter) => void;
  counts: Record<Filter, number>;
}) {
  const options: Array<[Filter, string]> = [
    ['all', 'All'],
    ['action', 'Needs action'],
    ['accepted', 'Accepted'],
    ['missing', 'Missing info'],
  ];
  return (
    <div role="tablist" aria-label="Filter deliverables" className="flex gap-2">
      {options.map(([key, text]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={value === key}
          onClick={() => onChange(key)}
          className={cn(
            'inline-flex h-[26px] items-center gap-1.5 rounded-full border px-3 text-meta font-medium',
            value === key
              ? 'border-ink-900 bg-ink-900 text-paper-0'
              : 'border-ink-100 bg-paper-100 text-ink-700 hover:border-ink-200',
          )}
        >
          {text}
          <span className="font-mono">{counts[key]}</span>
        </button>
      ))}
    </div>
  );
}

function NoDeliverables({
  engagementId,
  pendingCount,
  onAdd,
}: {
  engagementId: string;
  pendingCount: number;
  onAdd: () => void;
}) {
  const waiting = pendingCount > 0;
  return (
    <Card>
      <CardContent className="flex gap-4 p-6">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-card border',
            waiting
              ? 'border-ai-surface-border bg-ai-surface text-ai-ink'
              : 'border-ink-100 bg-paper-100 text-ink-500',
          )}
        >
          {waiting ? (
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Upload className="h-4 w-4" aria-hidden="true" />
          )}
        </div>
        <div className="space-y-3">
          <div>
            <h2 className="text-body font-semibold text-ink-900">
              {waiting
                ? `${pendingCount} extracted ${pendingCount === 1 ? 'item is' : 'items are'} waiting for review`
                : 'Deliverables come from the SOW'}
            </h2>
            <p className="mt-0.5 max-w-2xl text-body-sm text-ink-700">
              {waiting
                ? 'The AI read the whole SOW and proposed its deliverables, milestones, payment terms, KPIs and SLAs. Nothing is added here until you approve it.'
                : 'Upload the signed SOW. The AI reads every page and proposes each deliverable with its SOW reference, due date, acceptance criteria and payment tie for your review.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              asChild
              size="sm"
              className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
            >
              {waiting ? (
                <Link to="/engagements/$engagementId/proposals" params={{ engagementId }}>
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                  Review {pendingCount} {pendingCount === 1 ? 'proposal' : 'proposals'}
                </Link>
              ) : (
                <Link to="/engagements/$engagementId/documents" params={{ engagementId }}>
                  <Upload className="h-4 w-4" aria-hidden="true" />
                  Upload the SOW
                </Link>
              )}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onAdd}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add one manually
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
